import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const TICKET_DATA = {
  'TK-2026-001234': {
    id: 'TK-2026-001234',
    title: 'System downtime error 500',
    description:
      'The main application server is returning HTTP 500 errors. All users unable to access the system since 10:30 AM. This is affecting our entire operation.',
    customer: 'Acme Corp',
    status: 'Open',
    priority: 'High',
    category: 'System',
    created: '2026-04-20 10:30:00',
    updated: '2026-04-20 11:45:00',
    assignedTo: 'John Doe',
    sla: '4 hours',
    slaDue: '2026-04-20 14:30:00',
    activities: [
      {
        id: 1,
        author: 'You',
        type: 'created',
        content: 'Ticket created',
        timestamp: '2026-04-20 10:30:00',
      },
      {
        id: 2,
        author: 'John Doe',
        type: 'assigned',
        content: 'Ticket assigned to John Doe',
        timestamp: '2026-04-20 10:35:00',
      },
      {
        id: 3,
        author: 'John Doe',
        type: 'comment',
        content:
          'Investigating the server logs. Found high memory usage. Restarting services now.',
        timestamp: '2026-04-20 10:45:00',
      },
      {
        id: 4,
        author: 'John Doe',
        type: 'comment',
        content: 'Server restarted successfully. System is back online.',
        timestamp: '2026-04-20 11:00:00',
      },
      {
        id: 5,
        author: 'You',
        type: 'comment',
        content: 'Confirmed - system is working now. Thank you!',
        timestamp: '2026-04-20 11:15:00',
      },
    ],
  },
};

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const ticket = TICKET_DATA[id] || TICKET_DATA['TK-2026-001234'];
  const [replyText, setReplyText] = useState('');
  const [status, setStatus] = useState(ticket.status);

  const handleStatusChange = async (newStatus) => {
    setStatus(newStatus);
    // TODO: Update via API
    alert(`Status changed to ${newStatus}`);
  };

  const handleReply = (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    alert('Reply added! (Mock only)');
    setReplyText('');
  };

  const getStatusColor = (s) => {
    switch (s) {
      case 'Open':
        return { bg: '#dbeafe', color: '#1e3a8a' };
      case 'In Progress':
        return { bg: '#fef3c7', color: '#92400e' };
      case 'Resolved':
        return { bg: '#dcfce7', color: '#166534' };
      default:
        return { bg: '#f3f4f6', color: '#374151' };
    }
  };

  const getPriorityColor = (p) => {
    switch (p) {
      case 'Critical':
        return '#dc2626';
      case 'High':
        return '#ea580c';
      case 'Medium':
        return '#d97706';
      case 'Low':
        return '#65a30d';
      default:
        return '#6b7280';
    }
  };

  const statusColor = getStatusColor(status);

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <button
            onClick={() => navigate('/tickets')}
            style={styles.backBtn}
            title="Back to tickets"
          >
            ← Back
          </button>
          <h1 style={styles.title}>{ticket.id}</h1>
        </div>
      </header>

      {/* Main Content */}
      <main style={styles.content}>
        <div style={styles.mainCol}>
          {/* Ticket Info */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>{ticket.title}</h2>

            <div style={styles.ticketMeta}>
              <div style={styles.metaRow}>
                <span style={styles.metaLabel}>Status</span>
                <select
                  value={status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  style={{
                    ...styles.statusSelect,
                    backgroundColor: statusColor.bg,
                    color: statusColor.color,
                  }}
                >
                  <option>Open</option>
                  <option>In Progress</option>
                  <option>Resolved</option>
                </select>
              </div>

              <div style={styles.metaRow}>
                <span style={styles.metaLabel}>Priority</span>
                <span
                  style={{
                    ...styles.priorityBadge,
                    backgroundColor: getPriorityColor(ticket.priority),
                  }}
                >
                  {ticket.priority}
                </span>
              </div>

              <div style={styles.metaRow}>
                <span style={styles.metaLabel}>Customer</span>
                <span style={styles.metaValue}>{ticket.customer}</span>
              </div>

              <div style={styles.metaRow}>
                <span style={styles.metaLabel}>Category</span>
                <span style={styles.metaValue}>{ticket.category}</span>
              </div>

              <div style={styles.metaRow}>
                <span style={styles.metaLabel}>Assigned To</span>
                <span style={styles.metaValue}>{ticket.assignedTo}</span>
              </div>

              <div style={styles.metaRow}>
                <span style={styles.metaLabel}>SLA</span>
                <span style={styles.metaValue}>{ticket.sla}</span>
              </div>

              <div style={styles.metaRow}>
                <span style={styles.metaLabel}>Created</span>
                <span style={styles.metaValue}>{ticket.created}</span>
              </div>

              <div style={styles.metaRow}>
                <span style={styles.metaLabel}>Updated</span>
                <span style={styles.metaValue}>{ticket.updated}</span>
              </div>
            </div>
          </section>

          {/* Description */}
          <section style={styles.section}>
            <h3 style={styles.sectionSubtitle}>Description</h3>
            <p style={styles.description}>{ticket.description}</p>
          </section>

          {/* Activity Timeline */}
          <section style={styles.section}>
            <h3 style={styles.sectionSubtitle}>Activity Timeline</h3>
            <div style={styles.timeline}>
              {ticket.activities.map((activity, idx) => (
                <div key={activity.id} style={styles.activityItem}>
                  <div style={styles.activityDot} />
                  <div style={styles.activityContent}>
                    <div style={styles.activityHeader}>
                      <span style={styles.activityAuthor}>
                        {activity.author}
                      </span>
                      <span style={styles.activityType}>{activity.type}</span>
                    </div>
                    <p style={styles.activityText}>{activity.content}</p>
                    <span style={styles.activityTime}>
                      {activity.timestamp}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Reply Form */}
          <section style={styles.section}>
            <h3 style={styles.sectionSubtitle}>Add Reply</h3>
            <form onSubmit={handleReply} style={styles.replyForm}>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your message here..."
                style={styles.replyTextarea}
                rows="4"
              />
              <button
                type="submit"
                disabled={!replyText.trim()}
                style={{
                  ...styles.replyBtn,
                  opacity: !replyText.trim() ? 0.5 : 1,
                }}
              >
                Send Reply
              </button>
            </form>
          </section>
        </div>

        {/* Sidebar */}
        <aside style={styles.sidebar}>
          {/* Status & Priority Card */}
          <div style={styles.sidebarCard}>
            <div style={styles.statusSection}>
              <div style={styles.statusItem}>
                <label style={styles.statusLabel}>Status</label>
                <select
                  value={status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  style={{
                    ...styles.statusSelect,
                    backgroundColor: statusColor.bg,
                    color: statusColor.color,
                  }}
                >
                  <option>Open</option>
                  <option>In Progress</option>
                  <option>Resolved</option>
                </select>
              </div>
              <div style={styles.statusItem}>
                <label style={styles.statusLabel}>Priority</label>
                <span
                  style={{
                    ...styles.priorityBadge,
                    backgroundColor: getPriorityColor(ticket.priority),
                  }}
                >
                  {ticket.priority}
                </span>
              </div>
            </div>
          </div>

          {/* Details Card */}
          <div style={styles.sidebarCard}>
            <h4 style={styles.sidebarTitle}>Details</h4>
            <div style={styles.detailsList}>
              <div style={styles.detailItem}>
                <span style={styles.detailKey}>Customer</span>
                <span style={styles.detailValue}>{ticket.customer}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailKey}>Category</span>
                <span style={styles.detailValue}>{ticket.category}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailKey}>Assigned To</span>
                <span style={styles.detailValue}>{ticket.assignedTo}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailKey}>SLA</span>
                <span style={styles.detailValue}>{ticket.sla}</span>
              </div>
            </div>
          </div>

          {/* Timestamps Card */}
          <div style={styles.sidebarCard}>
            <h4 style={styles.sidebarTitle}>Timeline</h4>
            <div style={styles.detailsList}>
              <div style={styles.detailItem}>
                <span style={styles.detailKey}>Created</span>
                <span style={styles.detailValue}>{ticket.created}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailKey}>Updated</span>
                <span style={styles.detailValue}>{ticket.updated}</span>
              </div>
            </div>
          </div>

          {/* Quick Stats Card */}
          <div style={styles.sidebarCard}>
            <h4 style={styles.sidebarTitle}>Activity</h4>
            <div style={styles.statsList}>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Replies</span>
                <span style={styles.statNumber}>5</span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Views</span>
                <span style={styles.statNumber}>12</span>
              </div>
            </div>
          </div>

          {/* Related Articles Card */}
          <div style={styles.sidebarCard}>
            <h4 style={styles.sidebarTitle}>Related Articles</h4>
            <div style={styles.relatedList}>
              <a href="#" style={styles.relatedLink}>
                How to fix 500 errors
              </a>
              <a href="#" style={styles.relatedLink}>
                Server troubleshooting guide
              </a>
              <a href="#" style={styles.relatedLink}>
                Performance optimization
              </a>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f7fa',
    paddingBottom: '64px',
  },

  // Header
  header: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e8eef5',
    padding: '1rem 1.5rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  backBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.95rem',
    color: '#1e3a8a',
    fontWeight: '600',
    pointerEvents: 'auto',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1f2937',
    margin: 0,
  },

  // Content Layout
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem 1.5rem',
    display: 'grid',
    gridTemplateColumns: '1fr 320px',
    gap: '2rem',
  },
  mainCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },

  // Section Styles
  section: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '1.5rem',
    border: '1px solid #e8eef5',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: '#1f2937',
    margin: '0 0 1.5rem 0',
  },
  sectionSubtitle: {
    fontSize: '1rem',
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: '1rem',
  },

  // Ticket Meta
  ticketMeta: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1rem',
  },
  metaRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  metaLabel: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
  },
  metaValue: {
    fontSize: '0.95rem',
    fontWeight: '500',
    color: '#374151',
  },
  statusSelect: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    pointerEvents: 'auto',
  },
  priorityBadge: {
    display: 'inline-flex',
    padding: '0.4rem 0.75rem',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: 'white',
    width: 'fit-content',
  },

  // Description
  description: {
    fontSize: '0.95rem',
    lineHeight: '1.6',
    color: '#374151',
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },

  // Timeline
  timeline: {
    position: 'relative',
    paddingLeft: '2rem',
  },
  activityItem: {
    position: 'relative',
    paddingBottom: '1.5rem',
    marginBottom: '0.5rem',
  },
  'activityItem:not(:last-child)': {
    borderLeft: '2px solid #e5e7eb',
    paddingLeft: '1.5rem',
    marginLeft: '-2rem',
  },
  activityDot: {
    position: 'absolute',
    left: '-34px',
    top: '4px',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: '#1e3a8a',
    border: '3px solid #ffffff',
    boxShadow: '0 0 0 1px #e5e7eb',
  },
  activityContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  activityHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  activityAuthor: {
    fontWeight: '600',
    color: '#1f2937',
    fontSize: '0.875rem',
  },
  activityType: {
    fontSize: '0.75rem',
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
    padding: '0.2rem 0.5rem',
    borderRadius: '3px',
    textTransform: 'uppercase',
  },
  activityText: {
    fontSize: '0.95rem',
    color: '#374151',
    margin: '0.4rem 0 0 0',
  },
  activityTime: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    marginTop: '0.25rem',
  },

  // Reply Form
  replyForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  replyTextarea: {
    padding: '0.75rem 1rem',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    outline: 'none',
    resize: 'vertical',
    pointerEvents: 'auto',
  },
  'replyTextarea:focus': {
    borderColor: '#1e3a8a',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
  },
  replyBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#1e3a8a',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s',
    pointerEvents: 'auto',
    alignSelf: 'flex-start',
  },
  'replyBtn:hover:not(:disabled)': {
    backgroundColor: '#1e3a8a',
  },

  // Sidebar
  sidebarCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '1.25rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    marginBottom: '1rem',
  },
  sidebarTitle: {
    fontSize: '0.85rem',
    fontWeight: '800',
    color: '#1e3a8a',
    margin: '0 0 1rem 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  
  // Status & Priority Card
  statusSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  statusItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  statusLabel: {
    fontSize: '0.75rem',
    color: '#64748b',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  
  // Details Card
  detailsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  detailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: '1rem',
    borderBottom: '1px solid #f3f4f6',
  },
  'detailItem:last-child': {
    borderBottom: 'none',
    paddingBottom: 0,
  },
  detailKey: {
    fontSize: '0.85rem',
    color: '#64748b',
    fontWeight: '700',
    minWidth: '100px',
  },
  detailValue: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#1f2937',
    textAlign: 'right',
    flex: 1,
  },
  
  // Stats Card
  statsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  },
  statItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statLabel: {
    fontSize: '0.85rem',
    color: '#64748b',
    fontWeight: '700',
  },
  statNumber: {
    fontSize: '1.5rem',
    fontWeight: '800',
    color: '#1e3a8a',
  },
  relatedList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  relatedLink: {
    fontSize: '0.875rem',
    color: '#1e3a8a',
    textDecoration: 'none',
    transition: 'color 0.2s',
    padding: '0.5rem',
    borderRadius: '4px',
    pointerEvents: 'auto',
  },
  'relatedLink:hover': {
    color: '#1e3a8a',
    backgroundColor: '#f0f9ff',
  },
};
